import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useIdentity } from "../identity/useIdentity";

/**
 * Persistent org-name indicator + click-to-open menu containing logout.
 *
 * PRD-003 DoD:
 * - Renders the org name from `/identity` (idle and loading states show a
 *   placeholder so the chrome doesn't reflow).
 * - Click opens a small menu containing a "Log out" action.
 * - Logout clears the cached JWT (PRD-002) AND removes the cached
 *   `/identity` query so a new paste fires a fresh fetch.
 * - Org name only — no user identity or project context surfaced here.
 */
export function OrgIndicator() {
    const { jwt, logout } = useAuth();
    const { identity, status } = useIdentity();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleLogout = () => {
        setOpen(false);
        // Order is enforced by the auth:logout event bus and its subscribers
        // (see IdentityCacheCleaner and the logout flow in AuthContext):
        //   1. jwtStore.clear()
        //   2. emit auth:logout → subscribers run synchronously:
        //        - IdentityCacheCleaner: queryClient.removeQueries(['identity'])
        //        - PRD-004 (when built) will subscribe here for project state
        //   3. setIsModalOpen(true)
        // OrgIndicator just calls logout(). PRD-003 DoD #10 ordering is met.
        logout();
    };

    let label: string;
    if (jwt === null) {
        label = "Not signed in";
    } else if (status === "ready" && identity) {
        label = identity.organization.name;
    } else if (status === "error") {
        label = "Organization unavailable";
    } else {
        label = "Loading...";
    }

    return (
        <div className="indicator-wrap" ref={ref} data-testid="org-indicator">
            <button
                type="button"
                className="chrome-indicator"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Organization menu"
                onClick={() => setOpen((prev) => !prev)}
                disabled={jwt === null}
                data-testid="org-indicator-button"
            >
                <i className="fa-solid fa-building" aria-hidden="true" />
                <span>{label}</span>
                {jwt !== null && <i className="fa-solid fa-chevron-down" aria-hidden="true" />}
            </button>
            {open && (
                <div className="chrome-menu" role="menu">
                    <button
                        type="button"
                        role="menuitem"
                        onClick={handleLogout}
                        data-testid="logout-button"
                    >
                        <i className="fa-solid fa-right-from-bracket" aria-hidden="true" /> Log out
                    </button>
                </div>
            )}
        </div>
    );
}
